import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { LeaderboardEntry } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';

export default function Leaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getLeaderboard();

      if (response.success && response.data) {
        const data = response.data as any;
        const entries: LeaderboardEntry[] = Array.isArray(data)
          ? data
          : data.results || [];
        setLeaderboard(entries);
      } else {
        setError(response.error || 'Failed to load leaderboard');
      }
    } catch (err) {
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return '1';
      case 2:
        return '2';
      case 3:
        return '3';
      default:
        return `#${rank}`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadLeaderboard} className="mt-2 text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Leaderboard</h2>
        <div className="text-sm text-gray-500">Top learners</div>
      </div>

      {leaderboard.length === 0 ? (
        <EmptyState
          icon="🏆"
          title="No leaderboard data available yet."
          description="Complete lessons, finish courses, and pass exams to earn points."
          hintsTitle="How to earn points:"
          hints={[
            { icon: '📖', label: 'Complete lessons (+10)' },
            { icon: '🎓', label: 'Finish a course (+100)' },
            { icon: '📝', label: 'Pass an exam (+50)' },
            { icon: '🏅', label: 'Earn a new badge (+25)' },
          ]}
        />
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <div className="col-span-2">Rank</div>
              <div className="col-span-5">User</div>
              <div className="col-span-2">Points</div>
              <div className="col-span-3">Last Updated</div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {leaderboard.map((entry, index) => {
              const rank = index + 1;
              const isCurrentUser = user?.username === entry.username;

              return (
                <div
                  key={`${entry.username}-${index}`}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                    isCurrentUser ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-2">
                      <span className="text-lg font-bold">{getRankIcon(rank)}</span>
                    </div>

                    <div className="col-span-5">
                      <div className="text-sm font-medium text-gray-900">
                        @{entry.username}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="col-span-2">
                      <span className="text-lg font-bold text-gray-900">
                        {entry.points.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">pts</span>
                    </div>

                    <div className="col-span-3">
                      <span className="text-sm text-gray-500">
                        {new Date(entry.last_updated).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">How to earn points:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Complete lessons: +10 points per lesson</li>
          <li>• Finish courses: +100 points per course</li>
          <li>• Pass exams: +50 points per exam</li>
          <li>• Earn badges: +25 points per badge</li>
        </ul>
      </div>
    </div>
  );
}
